/**
 * The customer's bill.
 *
 * PRINTED ON AN 80MM THERMAL ROLL
 * -------------------------------
 * Which is about 48 characters wide and has no colour, no greys and no
 * background printing — a thermal head either burns a dot or it does not.
 * So this is set in a monospace face at a fixed width, uses rules rather than
 * shaded panels, and states everything in plain black.
 *
 * The screen version looks like the paper one on purpose. A cashier checking a
 * bill against a complaint should be looking at the same thing the customer
 * is holding.
 *
 * WHAT MUST BE ON IT
 * ------------------
 * Tax is broken out by authority and rate, not folded into the total. That is
 * a legal requirement here, and it is also what makes an FBR submission
 * possible later — the invoice already carries the fields, they are simply
 * empty until the registration comes through.
 */
import { formatPKRExact } from '@foodrun/shared';

import logo from '../../assets/logo-mono.png';

import { formatWhen, readableType, readableMethod } from './billFormat.js';

export function Bill({ bill }) {
  if (!bill) return null;

  const { company, branch, invoice, customer, items, totals, payments, cashier } = bill;

  return (
    <div className="bill mx-auto max-w-[302px] bg-white p-3 font-mono text-[11px] leading-snug text-black">
      {/* The day's serial, biggest thing in the corner — 1 for the first order
          after the day opens, counting up, back to 1 on the next day. */}
      {invoice?.serial != null && (
        <div className="text-right text-xl font-bold leading-none">#{invoice.serial}</div>
      )}

      <header className="text-center">
        {/*
          The mark, in one ink.

          A separate file rather than the colour logo filtered to grey, because
          the wordmark is half green and half white — and white on a white roll
          is nothing at all. In `logo-mono.png` everything the designer drew is
          black, whatever colour they drew it in, and the edges are hard rather
          than anti-aliased: a thermal head burns a dot or it does not, and a
          grey edge comes out as a ragged row of half-lit dots.

          Given a width in millimetres so it is the same size on the roll
          whatever resolution the printer works at, and a pixel width for the
          screen, which knows nothing about millimetres.
        */}
        <img
          src={logo}
          alt=""
          className="mx-auto mb-1.5 w-[45mm] max-w-[170px]"
        />

        <h1 className="text-sm font-bold uppercase tracking-wide">{company?.name}</h1>
        {company?.legalName && company.legalName !== company.name && (
          <p className="text-[10px]">{company.legalName}</p>
        )}
        <p className="mt-0.5">{branch?.name}</p>
        {branch?.address && <p className="text-[10px]">{branch.address}</p>}
        {branch?.phone && <p className="text-[10px]">{branch.phone}</p>}

        {/* Empty until the registrations come through, but printed the moment
            they do — a bill without them is not a valid tax invoice. */}
        {company?.ntn && <p className="text-[10px]">NTN: {company.ntn}</p>}
        {company?.strn && <p className="text-[10px]">STRN: {company.strn}</p>}
      </header>

      <Rule />

      <dl className="space-y-0.5">
        {/* The order number is the first thing anyone looks for, so it stands
            out on every bill, whatever the type. */}
        <Line label="Bill" value={invoice?.number} strong />
        {/* The Foodpanda app's own order number, on a Foodpanda bill — so the
            printed bill can be matched to the order in the app. */}
        {invoice?.foodpandaOrderNo && (
          <Line label="Foodpanda #" value={invoice.foodpandaOrderNo} strong />
        )}
        <Line label="Date" value={formatWhen(invoice?.date)} />
        <Line label="Type" value={readableType(invoice?.type)} />
        {invoice?.table && <Line label="Table" value={invoice.table} />}
        {cashier && <Line label="Served by" value={cashier} />}
        {customer?.name && customer.name !== 'Walk-in' && (
          <Line label="Customer" value={customer.name} strong />
        )}
        {/* Who and where — the rider carries the food to a door, and the packer
            checks the bag against a name and a number. Printed whenever they are
            on the order, which in practice is a delivery. Set larger and bold so
            they are read at a glance. */}
        {customer?.phone && <Line label="Phone" value={customer.phone} strong />}
        {customer?.address && (
          <div className="flex justify-between gap-2 text-[13px] font-bold">
            <dt className="shrink-0">Address</dt>
            <dd className="text-right">{customer.address}</dd>
          </div>
        )}
      </dl>

      <Rule />

      <table className="w-full">
        <thead>
          <tr className="text-left">
            <th className="font-normal">Item</th>
            <th className="w-8 text-center font-normal">Qty</th>
            <th className="w-16 text-right font-normal">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(items ?? []).map((item, index) => (
            // A refunded line stays on the bill, struck through, so the customer
            // can see which item the money went back on.
            <tr key={index} className={`align-top ${item.refunded ? 'line-through' : ''}`}>
              <td className="py-0.5 pr-1">
                {item.name}
                {item.refunded && <span className="no-underline"> (refunded)</span>}
                {item.modifiers && <span className="block text-[10px]">+ {item.modifiers}</span>}
              </td>
              <td className="py-0.5 text-center tabular-nums">{item.quantity}</td>
              <td className="py-0.5 text-right tabular-nums">{formatPKRExact(item.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Rule />

      <dl className="space-y-0.5">
        <Line label="Subtotal" value={formatPKRExact(totals?.subtotal ?? 0)} />

        {(totals?.discounts ?? []).map((discount, index) => (
          <Line
            key={index}
            label={
              /*
                The percentage, where it was one. "Discount 10%" beside
                "-Rs. 999.90" lets a customer check the sum on the spot;
                "-Rs. 999.90" alone is a figure they have to take on trust.
              */
              discount.reason ||
              (discount.type === 'percentage' ? `Discount (${discount.value}%)` : 'Discount')
            }
            value={`-${formatPKRExact(discount.amount ?? 0)}`}
          />
        ))}

        {/*
          The taxable amount, printed between the discount and the tax.

          This is the line the receipt did not have, and its absence is how a
          bill could show a discount and a tax figure that did not belong
          together for 116 orders. With it there, anyone can do the
          multiplication and see that the tax is right.
        */}
        {(totals?.totalDiscount ?? 0) > 0 && (
          <>
            <Rule />
            <Line
              label="Taxable amount"
              value={formatPKRExact(
                totals?.taxableAmount ?? (totals?.subtotal ?? 0) - (totals?.totalDiscount ?? 0)
              )}
            />
          </>
        )}

        {/* Broken out by authority and rate. Required, and the two active
            authorities charge differently. */}
        {(totals?.taxes ?? []).map((tax, index) => (
          <Line
            key={index}
            label={`${tax.name} (${tax.rate}%)`}
            value={`+${formatPKRExact(tax.amount ?? 0)}`}
          />
        ))}

        {totals?.deliveryFee > 0 && (
          <Line label="Delivery" value={formatPKRExact(totals.deliveryFee)} />
        )}
      </dl>

      <Rule />

      <div className="flex justify-between text-[13px] font-bold">
        <span>TOTAL</span>
        <span className="tabular-nums">{formatPKRExact(totals?.grandTotal ?? 0)}</span>
      </div>

      {/* Money handed back — a whole-bill refund or one or more single items —
          and what is left to owe once it comes off. Shown only once something
          has been refunded. */}
      {(totals?.refunded ?? 0) > 0 && (
        <>
          <div className="mt-0.5 flex justify-between text-[12px]">
            {/* Where the bill had tax, a refunded line gives back its tax too —
                so the figure is more than the line's ex-tax price shown above.
                Saying "incl. tax" stops that looking like a mistake. This shop
                runs tax off, so the note simply never appears. */}
            <span>Refunded{(totals?.totalTax ?? 0) > 0 ? ' (incl. tax)' : ''}</span>
            <span className="tabular-nums">-{formatPKRExact(totals.refunded)}</span>
          </div>
          <Rule />
          <div className="flex justify-between text-[13px] font-bold">
            <span>BALANCE</span>
            <span className="tabular-nums">{formatPKRExact(totals?.balance ?? 0)}</span>
          </div>
        </>
      )}

      {(payments ?? []).length > 0 && (
        <>
          <Rule />
          <dl className="space-y-0.5">
            {payments.map((payment, index) => (
              <Line
                key={index}
                label={readableMethod(payment.method)}
                value={formatPKRExact(payment.tendered ?? payment.amount)}
              />
            ))}
            {bill.changeAmount > 0 && (
              <Line label="Change" value={formatPKRExact(bill.changeAmount)} />
            )}
          </dl>
        </>
      )}

      <Rule />

      <footer className="text-center text-[10px]">
        <p>Thank you</p>
        {invoice?.status === 'refunded' && <p className="mt-1 font-bold">** REFUNDED **</p>}
      </footer>
    </div>
  );
}

/** A dotted rule, which is what a thermal printer renders cleanly. */
function Rule() {
  return <div aria-hidden="true" className="my-1.5 border-t border-dashed border-black" />;
}

function Line({ label, value, strong = false }) {
  // The lines a person reads first when a bill comes back to the counter or a
  // rider matches a bag to a door — the order number and who it is for — set a
  // notch larger and bold so they carry across a busy counter.
  return (
    <div className={`flex justify-between gap-2 ${strong ? 'text-[13px] font-bold' : ''}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
